import { createElement, useMemo, useState, type CSSProperties, type ReactNode } from 'react'
import {
  CalendarClock,
  Download,
  LayoutList,
  Lock,
  MessageSquare,
  Presentation,
  Printer,
  RefreshCw,
  RotateCcw,
  Settings,
  Share2,
  Sparkles,
  Upload,
  X,
} from 'lucide-react'
import type { AppWorkerClient } from '../../../data/AppWorkerClient'
import type { InsightDrillTarget, ServerApiClient } from '../../../data/ServerApiClient'
import { CfoInteriorPage } from './CfoInteriorPage'
import { cfoProFeaturesAvailable, useCfoBootstrap } from './useCfoBootstrap'
import { CfoProUpgradeNotice } from './CfoProUpgradeNotice'
import { CfoCardSkeletons, CfoControlRow, CfoSegmentedControl } from './CfoPagePrimitives'
import { buildCfoDuckDbOpts } from './cfoDuckDb'
import { formatAsOf } from './cfoFormat'
import { CFO_SURFACES } from './cfoTelemetry'
import type { CfoNavProfile } from './cfoNav'
import type { CfoRuntimeProfile } from './cfoRuntimeProfile'
import { useCfoAnalysis } from './analysis'
import { CfoSharingPanel } from '../CfoSharingPanel'
import {
  AccountsReceivableSection,
  RatioCardSection,
  BlockEditorDrawer,
  ConfigDrawer,
  FinancialRollupTable,
  KpiCard,
  QuarterlyRevenueNetIncomeChart,
  StatementTable,
  TrendBars,
  Waterfall,
  cleanText,
  formatAmount,
  formatPct,
  metricAt,
  normalizeWaterfalls,
  preparedReportIconFor,
  presentationCompanyName,
  presentationReportTitle,
  presentationSectionTitle,
  type PresentationReportBlock,
  type PresentationSectionKey,
} from '../QuickBooksCfoPresentationTab'
import { usePresentationStudio, type StudioOutput } from './presentation/usePresentationStudio'
import { EditableList, EditableProse } from './presentation/EditableProse'
import { CfoShareLinksPanel } from './presentation/CfoShareLinksPanel'
import { CfoSharedReportsInbox } from './presentation/CfoSharedReportsInbox'
import { KeyHighlightsSection } from './visuals/KeyHighlightsSection'
import './presentation/CfoPresentationStudio.css'

const SURFACE = CFO_SURFACES.presentations

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

/** Format a `YYYY-MM` period key as "May 2026". Falls back to the raw value. */
function formatMonthLabel(period: string | null | undefined): string {
  const raw = String(period ?? '').trim()
  const match = /^(\d{4})-(\d{2})/.exec(raw)
  if (!match) return raw
  const year = Number(match[1])
  const month = Number(match[2])
  if (month < 1 || month > 12) return raw
  return `${MONTH_NAMES[month - 1]} ${year}`
}

function isPartialReportingPeriod(value: string | null | undefined): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ''))
  if (!match) return false
  return Number(match[3]) < new Date(Date.UTC(Number(match[1]), Number(match[2]), 0)).getUTCDate()
}

export type CfoPresentationsPageProps = {
  appApi: ServerApiClient
  workerClient?: AppWorkerClient | null
  workbookId: string | null
  onNavSelect: (navId: string) => void
  onOpenCfoCore?: (target?: InsightDrillTarget | null) => void
  onOpenClassic: () => void
  classicEscapes?: boolean
  navProfile?: CfoNavProfile
  runtimeProfile?: CfoRuntimeProfile
}

export function CfoPresentationsPage({
  appApi,
  workerClient = null,
  workbookId,
  onNavSelect,
  onOpenCfoCore,
  onOpenClassic,
  classicEscapes = false,
  navProfile = 'full',
  runtimeProfile,
}: CfoPresentationsPageProps) {
  const studio = usePresentationStudio(appApi, workbookId, true)
  // The presentation endpoint enforces the hard entitlement boundary; this
  // parallel bootstrap only supplies the friendly upgrade state.
  const { bootstrap } = useCfoBootstrap(appApi, workbookId)
  const proFeatures = cfoProFeaturesAvailable(bootstrap)

  const [output, setOutput] = useState<StudioOutput>('report')
  const [builderOpen, setBuilderOpen] = useState(false)
  const [configOpen, setConfigOpen] = useState(false)
  const [agentOpen, setAgentOpen] = useState(false)

  const {
    response,
    config,
    setConfig,
    metrics,
    statements,
    period,
    summary,
    alerts,
    actions,
    caveats,
    sectionSummaries,
    selectedPeriodCount,
    profitAndLossRollup,
    quarterlyProfitAndLossRollup,
    balanceSheetRollup,
    cashFlowRollup,
    metricComparisons,
    insights,
    theme,
    topCustomerLimit,
    enabledSections,
    activeSection,
    setActiveSection,
    canEdit,
    saving,
    refreshing,
    statusMessage,
    loading,
    error,
    saveConfig,
    refreshReport,
    captureCurrentRevision,
    markReviewed,
    exportPdf,
    exportHtml,
    exportPptx,
    openSlideDeck,
    buildDeckHtml,
    setExecutiveSummary,
    setSectionSummary,
    setBlockText,
    setCommentaryList,
    agentMessage,
    setAgentMessage,
    agentBusy,
    agentError,
    agentTarget,
    setAgentTarget,
    lastAgentRunId,
    lastAgentSummary,
    submitAgentEdit,
    undoLastAgentEdit,
    revisions,
    selectedPeriod,
    viewingSnapshot,
    canManageMonths,
    publishing,
    rollingForward,
    selectPeriod,
    publish,
    rollForward,
    reportMaterializationEnabled,
    reportAiEditorEnabled,
  } = studio

  const asOfLabel = useMemo(() => formatAsOf(period) || period || '—', [period])
  const analysis = useCfoAnalysis()
  const periodOptions = useMemo(
    () => [
      { id: 'draft', label: `Draft${period ? ` · ${period}` : ''}` },
      ...revisions.map((revision) => ({
        id: revision.id,
        label: `${formatMonthLabel(revision.reporting_period)} v${revision.revision_number} · ${revision.status === 'issued' ? 'Issued' : 'Captured'}`,
      })),
    ],
    [revisions, period],
  )
  const selectedRevision = revisions.find((revision) => revision.id === selectedPeriod)
  const duckDbOpts = useMemo(() => buildCfoDuckDbOpts(workbookId), [workbookId])
  const deckHtml = useMemo(() => (output === 'slides' ? buildDeckHtml() : ''), [output, buildDeckHtml])

  const openAgentForBlock = (block: PresentationReportBlock) => {
    setAgentTarget({ section: block.section, blockId: block.id, title: block.title || presentationSectionTitle(config, block.section) })
    setAgentOpen(true)
  }
  const openInstantCardForBlock = (block: PresentationReportBlock) => {
    const title = cleanText(block.title) || presentationSectionTitle(config, block.section)
    const blockKind = cleanText(block.kind)
    const sectionTitle = presentationSectionTitle(config, block.section)
    const sourceTables = block.kind === 'key_highlights'
      ? ['cfo_insights', 'quickbooks_cfo_statements_render', 'quickbooks_cfo_ar_analysis']
      : block.kind === 'ar_summary'
      ? ['quickbooks_cfo_ar_analysis']
      : block.kind === 'statement_detail' || block.kind === 'statement_rollup' || block.kind === 'waterfall' || block.kind === 'trend_chart' || block.kind === 'kpi_grid'
        ? ['quickbooks_cfo_statements_render']
        : ['quickbooks_cfo_presentation']
    analysis.openAnalysis({
      seedPrompt: `Explain the selected presentation block "${title}" and recommend the most useful refinement.`,
      metricId: `report_block:${block.id}`,
      metricLabel: title,
      sourceComponent: 'report_block',
      sourceTables,
      selection: {
        interaction: 'evidence_click',
        selection_key: block.id,
        report_block_id: block.id,
        block_kind: block.kind,
        block_section: block.section,
        source_component: 'report_block',
        metric_id: `report_block:${block.id}`,
        metric_label: title,
        source_tables: sourceTables,
        source_fingerprint: cleanText(response?.generated_at || response?.binding_id || response?.layout_version_id || period || block.id),
        artifact_ids: [cleanText(response?.binding_id), cleanText(response?.layout_version_id), block.id].filter(Boolean),
        deterministic_read: `${title} is a ${blockKind || 'report'} block in ${sectionTitle}.`,
        instant_card_insights: [`${title} should be judged by whether it helps a reader understand the ${sectionTitle} story without adding duplicate detail.`],
        instant_card_prescriptive_actions: ['Refine the block only after checking whether its source facts, commentary, and intended audience are aligned.'],
        instant_card_next_steps: [
          {
            kind: 'report',
            label: 'Add to monthly report',
            prompt: `Tag ${title} as a report-ready block.`,
            target: { surface: SURFACE, reportBlockId: block.id },
          },
          {
            kind: 'ask',
            label: 'Refine this block',
            prompt: `Recommend a tighter version of the ${title} presentation block using only its surfaced facts and commentary.`,
            target: { surface: SURFACE, reportBlockId: block.id },
          },
          {
            kind: 'open_surface',
            label: 'Open presentations',
            prompt: `Open the presentation editor focused on ${title}.`,
            target: { surface: SURFACE, reportBlockId: block.id },
          },
        ],
        instant_card_change_explanations: [
          {
            periodKind: 'current_month',
            label: 'Selected block',
            status: 'partial',
            current: title,
            prior: '-',
            change: blockKind || 'report block',
            headline: `${title} is the selected ${blockKind || 'report'} block in ${sectionTitle}.`,
            drivers: [
              {
                id: block.id,
                label: sectionTitle,
                value: blockKind || 'report block',
                direction: 'flat',
                category: 'presentation_block',
                sourceSurface: SURFACE,
                target: { surface: SURFACE, reportBlockId: block.id },
              },
            ],
            confidence: 'medium',
            caveats: ['Presentation block cards explain report structure and source alignment; numeric period drivers come from the underlying financial surface.'],
            recommendedAction: 'Use the underlying financial card before changing numeric claims in this block.',
          },
        ],
      },
    })
  }
  const openAgentForSection = (sectionKey: PresentationSectionKey) => {
    setAgentTarget({ section: sectionKey, blockId: '', title: presentationSectionTitle(config, sectionKey) })
    setAgentOpen(true)
  }

  // ---- block renderers (dispatch mirrors the legacy live tab) -----------
  const statementForBlock = (block: PresentationReportBlock) => {
    const statementType = cleanText(block.source?.statement_type || (block.source as any)?.statementType)
    if (statementType === 'balance_sheet') return statements?.balance_sheet
    if (statementType === 'cash_flow') return statements?.cash_flow
    return statements?.profit_and_loss
  }
  const rollupForBlock = (block: PresentationReportBlock) => {
    const statementType = cleanText(block.source?.statement_type || (block.source as any)?.statementType)
    if (statementType === 'balance_sheet') return balanceSheetRollup
    if (statementType === 'cash_flow') return cashFlowRollup
    return profitAndLossRollup
  }

  const renderKpiBlock = (block: PresentationReportBlock): ReactNode => {
    if (block.section === 'balanceSheet') {
      return (
        <section className="fg-cfo-presentation-kpis" data-report-block-id={block.id}>
          <KpiCard label="Cash" value={formatAmount(metricAt(metrics, 'balanceSheet.cash'))} />
          <KpiCard label="Assets" value={formatAmount(metricAt(metrics, 'balanceSheet.totalAssets'))} />
          <KpiCard label="Liabilities" value={formatAmount(metricAt(metrics, 'balanceSheet.totalLiabilities'))} />
          <KpiCard label="Equity" value={formatAmount(metricAt(metrics, 'balanceSheet.totalEquity'))} />
        </section>
      )
    }
    if (block.section === 'cashFlow') {
      const netChange = metricAt(metrics, 'cashFlow.netCashChange')
      const revenue = metricAt(metrics, 'profitAndLoss.totalRevenue')
      const ending = metricAt(metrics, 'cashFlow.endingCash')
      return (
        <section className="fg-cfo-presentation-kpis" data-report-block-id={block.id}>
          <KpiCard label="Net cash change" value={formatAmount(netChange)} tone={(netChange || 0) >= 0 ? 'good' : 'watch'} />
          <KpiCard label="Ending cash" value={formatAmount(ending)} />
          <KpiCard label="Revenue conversion" value={formatPct(netChange != null && revenue ? (netChange || 0) / (revenue || 1) : null)} />
          <KpiCard label="Runway signal" value={ending == null ? 'N/A' : (ending >= 0 ? 'Positive' : 'Deficit')} />
        </section>
      )
    }
    const netIncome = metricAt(metrics, 'profitAndLoss.netIncome')
    return (
      <section className="fg-cfo-presentation-kpis" data-report-block-id={block.id}>
        <KpiCard label={block.section === 'home' ? 'Total revenue' : 'Revenue'} value={formatAmount(metricAt(metrics, 'profitAndLoss.totalRevenue'))} detail={block.section === 'home' ? 'Latest period' : undefined} tone="good" />
        <KpiCard label={block.section === 'home' ? 'Net income' : 'Gross profit'} value={formatAmount(block.section === 'home' ? netIncome : metricAt(metrics, 'profitAndLoss.grossProfit'))} detail={block.section === 'home' ? formatPct(metricAt(metrics, 'profitAndLoss.netMarginPct')) : undefined} tone={(netIncome || 0) >= 0 ? 'good' : 'watch'} />
        <KpiCard label="EBITDA" value={formatAmount(metricAt(metrics, 'profitAndLoss.ebitda') ?? profitAndLossRollup.rows.find((row) => row.key === 'ebitda')?.values.at(-1))} detail={block.section === 'home' ? 'Analytical non-GAAP metric' : undefined} />
        <KpiCard label="Gross margin" value={formatPct(metricAt(metrics, 'profitAndLoss.grossMarginPct'))} detail={block.section === 'home' ? 'Revenue quality' : undefined} />
      </section>
    )
  }

  const renderTextBlock = (block: PresentationReportBlock): ReactNode => {
    const commentaryKey = cleanText(block.source?.commentary)
    const commentarySection = cleanText(block.source?.commentary_section || (block.source as any)?.commentarySection)
    const value = commentaryKey === 'executiveSummary'
      ? summary
      : commentarySection
        ? cleanText(sectionSummaries[commentarySection])
        : cleanText(block.content?.text)
    const onSave = commentaryKey === 'executiveSummary'
      ? (next: string) => setExecutiveSummary(next)
      : commentarySection
        ? (next: string) => setSectionSummary(commentarySection, next)
        : (next: string) => setBlockText(block.id, next)
    if (!value && !canEdit) return null
    return (
      <section className="fg-cfo-presentation-summary" data-report-block-id={block.id} data-editor-empty={!value ? 'true' : undefined}>
        <h2>{block.title}</h2>
        <EditableProse value={value} canEdit={canEdit} onSave={onSave} ariaLabel={block.title || 'commentary'} />
      </section>
    )
  }

  const renderActionList = (block: PresentationReportBlock): ReactNode => (
    <section className="fg-cfo-presentation-actions" data-report-block-id={block.id}>
      <EditableList heading="Alerts" items={alerts} canEdit={canEdit} onSave={(items) => setCommentaryList('alerts', items)} />
      <EditableList heading="Priorities" items={actions} canEdit={canEdit} onSave={(items) => setCommentaryList('actions', items)} />
      <EditableList heading="Data Caveats" items={caveats} extra={response?.warnings || []} canEdit={canEdit} onSave={(items) => setCommentaryList('dataCaveats', items)} />
    </section>
  )

  const renderBlockContent = (block: PresentationReportBlock): ReactNode => {
    if (block.kind === 'key_highlights') {
      return (
        <KeyHighlightsSection
          highlights={response?.key_highlights || []}
          evidence={response?.visual_evidence || []}
          rejections={(response as any)?.visual_selection_diagnostics?.rejections || []}
          onDrill={(highlight) => {
            if (onOpenCfoCore) onOpenCfoCore(highlight.drillTarget)
            else onNavSelect(cleanText(highlight.drillTarget?.tab) || 'home')
          }}
        />
      )
    }
    if (block.kind === 'kpi_grid') return renderKpiBlock(block)
    if (block.kind === 'text') return renderTextBlock(block)
    if (block.kind === 'action_list') return renderActionList(block)
    if (block.kind === 'waterfall') {
      return (
        <>
          {normalizeWaterfalls(config.waterfalls).filter((entry) => entry.enabled !== false).map((waterfall) => (
            <Waterfall key={`${block.id}-${waterfall.id}`} statement={statements?.profit_and_loss} metrics={metrics} periodCount={selectedPeriodCount} config={waterfall} comparisons={metricComparisons} />
          ))}
        </>
      )
    }
    if (block.kind === 'statement_rollup') {
      return <FinancialRollupTable title={block.title} rollup={rollupForBlock(block)} />
    }
    if (block.kind === 'statement_detail') {
      return <StatementTable title={block.title} statement={statementForBlock(block)} limit={Math.max(1, Math.min(80, Math.floor(Number(block.layout?.limit) || 32)))} periodCount={selectedPeriodCount} />
    }
    if (block.kind === 'trend_chart') {
      const metric = cleanText(block.source?.metric)
      if (metric === 'quarterly_revenue_net_income') {
        return <QuarterlyRevenueNetIncomeChart rollup={quarterlyProfitAndLossRollup} />
      }
      return <TrendBars statement={statementForBlock(block)} rowLabel={cleanText(block.source?.row_label || (block.source as any)?.rowLabel) || 'net income'} title={block.title} periodCount={selectedPeriodCount} />
    }
    if (block.kind === 'ar_summary') {
      return (
        <AccountsReceivableSection
          analysis={response?.ar_analysis}
          commentary={cleanText(sectionSummaries.accountsReceivable)}
          topCustomerLimit={topCustomerLimit}
        />
      )
    }
    if (block.kind === 'ratio_card') {
      // Was missing entirely: the server accepts and serves `ratio_card`, and
      // both export paths render it, but this dispatch returned null — so a
      // pinned ratio card silently vanished from the studio while appearing in
      // the exported pack. Mirrors the legacy tab's RatioCardSection.
      return <RatioCardSection block={block} ratioCards={response?.ratio_cards} canEdit={canEdit} />
    }
    return null
  }

  const renderBlock = (block: PresentationReportBlock): ReactNode => {
    const content = renderBlockContent(block)
    if (!content) return null
    return (
      <div key={block.id} className="cfo-studio-block" data-report-block-id={block.id} data-report-section={block.section}>
        {canEdit ? (
          <div className="cfo-studio-block-actions">
            <button
              type="button"
              className="cfo-studio-block-ai"
              title={`Instant read for ${block.title}`}
              aria-label={`Instant read for ${block.title}`}
              data-testid="cfo-presentation-block-instant"
              data-block-id={block.id}
              onClick={() => openInstantCardForBlock(block)}
            >
              <Sparkles size={14} />
            </button>
            <button
              type="button"
              className="cfo-studio-block-ai cfo-studio-block-edit-ai"
              title={`Edit ${block.title} with AI`}
              aria-label={`Edit ${block.title} with AI`}
              data-testid="cfo-presentation-block-ai"
              data-block-id={block.id}
              onClick={() => openAgentForBlock(block)}
            >
              <MessageSquare size={14} />
            </button>
          </div>
        ) : null}
        {content}
      </div>
    )
  }

  const renderSection = (sectionKey: PresentationSectionKey): ReactNode => {
    const blocks = (config.reportBlocks || []).filter((block) => block.enabled !== false && block.section === sectionKey)
    const rendered = blocks.map(renderBlock).filter(Boolean)
    if (!rendered.length) return null
    return (
      <section key={sectionKey} id={`cfo-studio-section-${sectionKey}`} className="cfo-studio-doc-section" data-section={sectionKey}>
        <div className="cfo-studio-doc-section-head">
          <h2>{presentationSectionTitle(config, sectionKey)}</h2>
          {canEdit && reportAiEditorEnabled ? (
            <button type="button" className="cfo-studio-section-ai" onClick={() => openAgentForSection(sectionKey)} title={`Ask AI about ${presentationSectionTitle(config, sectionKey)}`}>
              <Sparkles size={13} /> Ask AI
            </button>
          ) : null}
        </div>
        {rendered}
      </section>
    )
  }

  // ----- the report document (shared by report + share previews) ---------
  const reportDocument = (
    <div
      className="cfo-studio-paper"
      data-theme={theme.themeId}
      data-highlight-insight-count={insights?.cards?.length || 0}
      data-highlight-evidence-count={response?.visual_evidence?.length || 0}
      data-highlight-count={response?.key_highlights?.length || 0}
      style={theme.cssVars as CSSProperties}
      data-testid="cfo-presentation-paper"
    >
      <header className="cfo-studio-masthead">
        <div>
          <span className="cfo-studio-eyebrow">Performance review</span>
          <h1>{presentationReportTitle(config)}</h1>
          <p>
            {presentationCompanyName(config, response)}
            {period ? ` · ${period}` : ''}
            {config.branding?.subtitle ? ` · ${config.branding.subtitle}` : ''}
          </p>
        </div>
        <div className="cfo-studio-seal">
          {createElement(preparedReportIconFor(config.branding?.preparedReportIcon), { size: 26 })}
          <span>{cleanText(config.branding?.preparedReportTitle) || 'Prepared report'}</span>
        </div>
      </header>
      {enabledSections.map((entry) => renderSection(entry.key))}
    </div>
  )

  const headerLede = (
    <>
      “Your board pack is already written. <em>Curate the blocks, add commentary, and share.</em>”
    </>
  )

  return (
    <CfoInteriorPage
      appApi={appApi}
      workerClient={workerClient}
      workbookId={workbookId}
      navId="presentations"
      activeSection={SURFACE}
      runtimeProfile={runtimeProfile}
      eyebrow="Share"
      title="Presentations"
      lede={headerLede}
      crumb={(
        <>
          <b>Presentations</b> · Board pack
        </>
      )}
      asOfLabel={asOfLabel}
      periodOptions={periodOptions}
      periodValue={selectedPeriod || 'draft'}
      onPeriodChange={(value) => { void selectPeriod(value) }}
      periodAriaLabel="Report month"
      cacheState={null}
      pageLoading={loading}
      pageLoadingLabel="Loading the board pack…"
      copilotSubtitle="drafting your board pack"
      copilotPlaceholder="Ask to draft or reword a section…"
      copilotIntro="You’re on Presentations. Ask me to reword the summary, add a section, or switch the theme to match your firm’s branding."
      askPlaceholder="Ask to draft or reword a section…"
      suggestedPrompts={['Make the summary more concise', 'Add a cash runway chart', 'Tighten the priorities']}
      instantCards={(response?.instant_cards as any) ?? null}
      pageFactsFingerprint={response ? `${response.binding_id}:${response.layout_version_id}:${response.generated_at}` : null}
      duckDbOpts={duckDbOpts}
      onNavSelect={onNavSelect}
      navProfile={navProfile}
    >
      {() => (
        !proFeatures ? (
          <CfoProUpgradeNotice appApi={appApi} workbookId={workbookId} title="Board packs are part of Pro">
            Turn the close into a board-ready pack — AI-drafted narrative, your branding, and no-login share links. Board packs come with Pro.
          </CfoProUpgradeNotice>
        ) : (
        <div className="cfo-studio" data-testid="cfo-presentation-tab" data-can-edit={canEdit ? 'true' : 'false'}>
          <CfoControlRow>
            <CfoSegmentedControl
              label="Output"
              value={output}
              onChange={(value) => setOutput(value as StudioOutput)}
              options={[
                { id: 'report', label: 'HTML report' },
                { id: 'slides', label: 'Slide deck' },
                { id: 'share', label: 'Share' },
              ]}
            />
            <div className="cfo-studio-bar-actions" data-testid="cfo-presentation-toolbar">
              <button type="button" className="cfo-shell-chip" onClick={refreshReport} disabled={refreshing} data-testid="cfo-presentation-refresh">
                <RefreshCw size={14} /> {refreshing ? 'Refreshing…' : 'Refresh'}
              </button>
              {/* Gated on `canEdit` alone, matching Configure. This opens
                  BlockEditorDrawer — the audience presets (Full Review /
                  Investor / Board / Owner) and the sortable, deletable block
                  list. It was gated behind REPORT_AI_EDITOR_ENABLED, which is
                  `import.meta.env.DEV || VITE_...=true`, so the builder was
                  visible locally and had never appeared in a deployed build.
                  The coupling was incidental: the drawer edits
                  `config.reportBlocks` directly and calls no AI path. The three
                  genuine AI surfaces on this page stay behind that flag. */}
              {canEdit ? (
                <button type="button" className="cfo-shell-chip" onClick={() => setBuilderOpen(true)} data-testid="cfo-presentation-build">
                  <LayoutList size={14} /> Build report
                </button>
              ) : null}
              {canEdit ? (
                <button type="button" className="cfo-shell-chip" onClick={() => setConfigOpen(true)} data-testid="cfo-presentation-configure">
                  <Settings size={14} /> Configure
                </button>
              ) : null}
              {canEdit ? (
                <button type="button" className="cfo-shell-chip" onClick={() => { setAgentTarget(null); setAgentOpen(true) }} data-testid="cfo-presentation-agent-open">
                  <MessageSquare size={14} /> Ask AI
                </button>
              ) : null}
              {canEdit && reportAiEditorEnabled && lastAgentRunId ? (
                <button type="button" className="cfo-shell-chip" onClick={undoLastAgentEdit} disabled={agentBusy} title={lastAgentSummary || 'Undo last AI report edit'} data-testid="cfo-presentation-agent-undo">
                  <RotateCcw size={14} /> Undo AI
                </button>
              ) : null}
              {canManageMonths && !viewingSnapshot ? (
                <>
                  <button type="button" className="cfo-shell-chip" onClick={() => { void captureCurrentRevision().then(async (result) => { await studio.loadRevisions(); studio.setStatusMessage(`Created ${result.revision.label}.`) }).catch((err) => studio.setStatusMessage(`Version failed: ${cleanText(err?.message || err)}`)) }} title="Capture the displayed report as an immutable version" data-testid="cfo-presentation-create-version">
                    <Lock size={14} /> Create version
                  </button>
                  <button type="button" className="cfo-shell-chip" onClick={() => { void publish() }} disabled={publishing} title="Create an immutable issued version" data-testid="cfo-presentation-publish">
                    <Upload size={14} /> {publishing ? 'Issuing…' : 'Issue report'}
                  </button>
                  <button type="button" className="cfo-shell-chip" onClick={() => { void markReviewed() }} title="Use this exact governed state as the next comparison baseline" data-testid="cfo-presentation-mark-reviewed">
                    <Lock size={14} /> Mark reviewed
                  </button>
                  <button type="button" className="cfo-shell-chip" onClick={() => { void rollForward() }} disabled={rollingForward} title="Start next month from this layout & commentary" data-testid="cfo-presentation-roll-forward">
                    <CalendarClock size={14} /> {rollingForward ? 'Rolling…' : 'Roll forward'}
                  </button>
                </>
              ) : null}
              {output !== 'share' && reportMaterializationEnabled ? (
                <>
                  <button type="button" className="cfo-shell-chip" onClick={exportPdf} data-testid="cfo-presentation-export">
                    <Printer size={14} /> Download PDF
                  </button>
                  <button type="button" className="cfo-shell-chip" onClick={exportHtml} data-testid="cfo-presentation-export-html">
                    <Download size={14} /> Download HTML
                  </button>
                </>
              ) : null}
              {output === 'slides' ? (
                <>
                  <button type="button" className="cfo-shell-chip" onClick={openSlideDeck} data-testid="cfo-presentation-deck">
                    <Presentation size={14} /> Open deck
                  </button>
                  {reportMaterializationEnabled ? (
                    <button type="button" className="cfo-shell-chip" onClick={() => { void exportPptx() }} title="Download an editable PowerPoint deck" data-testid="cfo-presentation-export-pptx">
                      <Download size={14} /> Download PowerPoint
                    </button>
                  ) : null}
                </>
              ) : null}
            </div>
          </CfoControlRow>

          {error || !response ? (
            loading ? (
              <CfoCardSkeletons count={4} label="Drafting the board pack…" />
            ) : (
              <div className="cfo-shell-notice" data-testid="cfo-presentation-error">
                {error || 'Client financials are not configured.'}
                {classicEscapes ? (
                  <>
                    {' '}
                    <button type="button" className="btn ghost" onClick={onOpenClassic}>Open report workspace</button>
                  </>
                ) : null}
              </div>
            )
          ) : (
            <div className="cfo-studio-stage">
              {response.source_state ? (
                <div className="cfo-studio-data-state" aria-label="Live report data state">
                  <span><b>Reporting period</b>{formatMonthLabel(response.source_state.reporting_period || period)}</span>
                  <span><b>Data through</b>{response.source_state.data_through || period || '—'}</span>
                  <span><b>Data state</b>{response.source_state.new_data_available ? 'Stale' : isPartialReportingPeriod(response.source_state.data_through) ? 'Data arriving' : 'Current'}</span>
                  <span><b>Last refresh</b>{response.source_state.last_successful_refresh ? new Date(response.source_state.last_successful_refresh).toLocaleString() : '—'}</span>
                  <span><b>Review</b>{response.source_state.review_state?.replace(/_/g, ' ') || 'Needs review'}</span>
                  <span><b>Last reviewed</b>{response.source_state.last_reviewed_at ? new Date(response.source_state.last_reviewed_at).toLocaleString() : 'Not reviewed'}</span>
                  <span><b>Reviewed by</b>{response.source_state.last_reviewer_name || '—'}</span>
                  <span title={response.source_state.signature}><b>Source signature</b>{response.source_state.signature.slice(0, 12)}…</span>
                  <span><b>Reconciliation</b>{response.source_state.reconciliation_status?.replace(/_/g, ' ') || 'Needs review'}</span>
                  <span><b>Close readiness</b>{response.source_state.close_readiness_status?.replace(/_/g, ' ') || 'Needs review'}</span>
                  <span><b>Forecast</b>{response.source_state.forecast_included ? 'Included' : 'Not included'}</span>
                  <span><b>Latest issued</b>{response.source_state.latest_issued_revision_label || 'Not issued'}</span>
                  {response.source_state.new_data_available ? <button type="button" onClick={refreshReport}>New data available · refresh</button> : null}
                </div>
              ) : null}
              {viewingSnapshot ? (
                <div className="cfo-studio-readonly" data-testid="cfo-presentation-readonly">
                  <Lock size={14} />
                  <span><strong>{selectedRevision ? `${formatMonthLabel(selectedRevision.reporting_period)} v${selectedRevision.revision_number}` : 'This version'}</strong> is frozen and immutable. Switch to <button type="button" className="cfo-studio-linkbtn" onClick={() => { void selectPeriod('draft') }}>the live report</button> to edit.</span>
                </div>
              ) : null}
              <div className="cfo-studio-sectionbar" aria-label="Board pack sections">
                <div className="cfo-studio-sectionbar-head">
                  <div className="cfo-studio-sectionbar-meta">
                    <span className="cfo-studio-rail-label">In this pack</span>
                    <p className="cfo-studio-rail-meta">
                      {presentationCompanyName(config, response)}
                      {period ? <span className="cfo-studio-rail-sub"> · {period}</span> : null}
                    </p>
                  </div>
                  {canEdit ? (
                    <button type="button" className="cfo-studio-share-cta" onClick={() => setOutput('share')}>
                      <Share2 size={14} /> Share &amp; access
                    </button>
                  ) : null}
                </div>
                <nav>
                  {enabledSections.map((entry) => {
                    const Icon = entry.icon
                    const count = (config.reportBlocks || []).filter((b) => b.enabled !== false && b.section === entry.key).length
                    return (
                      <button
                        key={entry.key}
                        type="button"
                        data-active={activeSection === entry.key ? 'true' : 'false'}
                        onClick={() => {
                          setActiveSection(entry.key)
                          document.getElementById(`cfo-studio-section-${entry.key}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                        }}
                      >
                        <Icon size={15} />
                        <span>{presentationSectionTitle(config, entry.key)}</span>
                        {count ? <em>{count}</em> : null}
                      </button>
                    )
                  })}
                </nav>
              </div>

              <div className="cfo-studio-canvas">
                {output === 'report' ? reportDocument : null}
                {output === 'slides' ? (
                  <div className="cfo-studio-deck" data-testid="cfo-presentation-deck-preview">
                    {deckHtml ? (
                      <iframe title="Slide deck preview" srcDoc={deckHtml} />
                    ) : (
                      <div className="cfo-shell-notice">Build a report block to preview slides.</div>
                    )}
                  </div>
                ) : null}
                {output === 'share' ? (
                  <div className="cfo-studio-share" data-testid="cfo-presentation-share">
                    {workbookId ? (
                      <>
                        <CfoShareLinksPanel
                          appApi={appApi}
                          workbookId={workbookId}
                          captureRevision={async () => (await captureCurrentRevision()).revision.id}
                        />
                        <CfoSharingPanel appApi={appApi} workbookId={workbookId} onClose={() => setOutput('report')} />
                      </>
                    ) : (
                      <div className="cfo-shell-notice">Bind a workbook to share this report.</div>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          )}

          {response && output === 'share' ? <CfoSharedReportsInbox appApi={appApi} currentWorkbookId={workbookId} /> : null}

          {statusMessage ? <div className="cfo-studio-status" role="status" data-testid="cfo-presentation-status">{statusMessage}</div> : null}

          {/* Reused inspector surfaces (curate / configure) */}
          {builderOpen && canEdit ? (
            <BlockEditorDrawer
              config={config}
              onChange={setConfig}
              onClose={() => setBuilderOpen(false)}
              onSave={async () => { await saveConfig(); setBuilderOpen(false) }}
              saving={saving}
            />
          ) : null}
          {configOpen && canEdit ? (
            <ConfigDrawer
              appApi={appApi}
              config={config}
              workbookId={workbookId}
              savedViews={studio.savedViews}
              onChange={setConfig}
              onClose={() => setConfigOpen(false)}
              onSave={saveConfig}
              saving={saving}
            />
          ) : null}

          {/* AI report editor */}
          {agentOpen && canEdit && reportAiEditorEnabled ? (
            <aside className="fg-cfo-presentation-agent cfo-studio-agent" data-testid="cfo-presentation-agent-panel">
              <div className="fg-cfo-presentation-config-head">
                <div>
                  <span>AI report editor</span>
                  <strong>{agentTarget?.title || presentationSectionTitle(config, activeSection as PresentationSectionKey)}</strong>
                </div>
                <button type="button" aria-label="Close AI report editor" onClick={() => setAgentOpen(false)}><X size={16} /></button>
              </div>
              <label>
                Change request
                <textarea
                  rows={5}
                  value={agentMessage}
                  onChange={(event) => setAgentMessage(event.target.value)}
                  placeholder={`Example: refine ${agentTarget?.title || presentationSectionTitle(config, activeSection as PresentationSectionKey)} or add supporting detail`}
                  data-testid="cfo-presentation-agent-message"
                />
              </label>
              {agentError ? <div className="fg-cfo-presentation-ai-error" data-testid="cfo-presentation-agent-error">{agentError}</div> : null}
              <button
                type="button"
                className="fg-cfo-presentation-primary"
                disabled={agentBusy}
                data-testid="cfo-presentation-agent-submit"
                onClick={async () => {
                  const ok = await submitAgentEdit(agentMessage, agentTarget)
                  if (ok) setAgentOpen(false)
                }}
              >
                <Sparkles size={15} />
                {agentBusy ? 'Applying…' : 'Apply AI edit'}
              </button>
            </aside>
          ) : null}
        </div>
        )
      )}
    </CfoInteriorPage>
  )
}

export default CfoPresentationsPage
